import { View, StyleSheet, Font } from "@react-pdf/renderer";
import regular from "@packages/assets/font-family/Montserrat-Regular.ttf";
import bold from "@packages/assets/font-family/Montserrat-Bold.ttf";
import { useTranslation } from "react-i18next";
import { PdfLayout } from "../pdf/PdfLayout";
import MarkdownRenderer from "../custom-markdown";
import { CollaborativeGroupWorkResponseDto } from "@packages/api/madlenAPI.schemas";
import { PdfCustomizationSettings } from "@features/common/components/pdf-customization-modal";
import { scaleFontSize } from "../pdf/fontSizeAdjustment";

Font.register({
  family: "Montserrat",
  fonts: [{ src: regular }, { src: bold, fontWeight: "bold" }],
});

interface CollaborativeCardProps {
  collaborative: CollaborativeGroupWorkResponseDto;
  customizationSettings: PdfCustomizationSettings;
}

const styles = StyleSheet.create({
  section: {
    marginBottom: 15,
    padding: 12,
    backgroundColor: "#ffffff",
    borderRadius: 8,
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },
  rolesSection: {
    backgroundColor: "#fef3c7",
  },
  stepsSection: {
    backgroundColor: "#dcfce7",
  },
  reflectionSection: {
    backgroundColor: "#f3e8ff",
  },
  evaluationSection: {
    backgroundColor: "#dbeafe",
  },
  teacherRoleSection: {
    backgroundColor: "#f0f9ff",
  },
  teacherEvaluationSection: {
    backgroundColor: "#fffbeb",
  },
  processEvaluationSection: {
    backgroundColor: "#f0fdf4",
  },
  sectionTitle: {
    fontSize: 11,
    fontWeight: "bold",
    marginBottom: 8,
  },
  rolesTitle: {
    color: "#d97706",
  },
  stepsTitle: {
    color: "#16a34a",
  },
  reflectionTitle: {
    color: "#9333ea",
  },
  evaluationTitle: {
    color: "#2563eb",
  },
  teacherRoleTitle: {
    color: "#0369a1",
  },
  teacherEvaluationTitle: {
    color: "#ea580c",
  },
  processEvaluationTitle: {
    color: "#059669",
  },
  contentText: {
    fontSize: 9,
    color: "#374151",
    lineHeight: 1.4,
  },
  itemContainer: {
    marginLeft: 8,
    marginBottom: 8,
    borderLeftWidth: 1,
    borderLeftColor: "#e5e7eb",
    paddingLeft: 8,
  },
  noContentText: {
    fontSize: 9,
    color: "#9ca3af",
    fontStyle: "italic",
    lineHeight: 1.4,
  },
});

const CollaborativeDocument = ({
  collaborative,
  customizationSettings,
}: CollaborativeCardProps) => {
  const { t } = useTranslation();

  const renderContent = (
    content: string | null | undefined,
    fallback: string = "",
  ) => {
    if (
      !content ||
      content.trim() === "" ||
      content.toLowerCase().includes("no content")
    ) {
      return fallback || t("collaborative.noContent");
    }
    return content;
  };

  return (
    <PdfLayout
      courseName={collaborative.courseName || ""}
      gradeLevel={collaborative.gradeLevel || 0}
      topic={collaborative.title || ""}
      imageUrl={collaborative.imageUrl}
      author={collaborative.authorName || ""}
      createdDate={collaborative.createdAt || ""}
      headerTextColor="#d97706"
      customizationSettings={customizationSettings}
    >
      {/* Group Roles Section */}
      {collaborative.groupRoles && collaborative.groupRoles.length > 0 && (
        <View style={[styles.section, styles.rolesSection]}>
          <MarkdownRenderer
            content={t("collaborative.groupRoles")}
            customStyle={{
              text: {
                text: {
                  ...styles.sectionTitle,
                  ...styles.rolesTitle,
                  fontSize: scaleFontSize(
                    styles.sectionTitle.fontSize,
                    customizationSettings?.fontSize,
                  ),
                },
                container: {
                  marginBottom: 8,
                },
              },
            }}
          />
          {collaborative.groupRoles.map((role, index) => (
            <View key={index} style={styles.itemContainer}>
              <MarkdownRenderer
                content={renderContent(role.title)}
                customStyle={{
                  text: {
                    text: {
                      ...styles.contentText,
                      fontSize: scaleFontSize(
                        styles.contentText.fontSize,
                        customizationSettings?.fontSize,
                      ),
                      fontWeight: "bold",
                    },
                    container: {
                      marginBottom: 4,
                    },
                  },
                }}
              />
              <MarkdownRenderer
                content={
                  t("collaborative.responsibilities") +
                  ": " +
                  renderContent(role.responsibilities)
                }
                customStyle={{
                  text: {
                    text: {
                      ...styles.contentText,
                      fontSize: scaleFontSize(
                        styles.contentText.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                    container: {
                      marginBottom: 4,
                    },
                  },
                }}
              />
              <MarkdownRenderer
                content={
                  t("collaborative.skillsDeveloped") +
                  ": " +
                  renderContent(role.skills_developed)
                }
                customStyle={{
                  text: {
                    text: {
                      ...styles.contentText,
                      fontSize: scaleFontSize(
                        styles.contentText.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                    container: {
                      marginBottom: 4,
                    },
                  },
                }}
              />
            </View>
          ))}
        </View>
      )}

      {/* Process Steps Section */}
      {collaborative.processSteps && collaborative.processSteps.length > 0 && (
        <View style={[styles.section, styles.stepsSection]} wrap={false}>
          <MarkdownRenderer
            content={t("collaborative.processSteps")}
            customStyle={{
              text: {
                text: {
                  ...styles.sectionTitle,
                  ...styles.stepsTitle,
                  fontSize: scaleFontSize(
                    styles.sectionTitle.fontSize,
                    customizationSettings?.fontSize,
                  ),
                },
                container: {
                  marginBottom: 8,
                },
              },
            }}
          />
          {collaborative.processSteps.map((step, index) => (
            <View key={index} style={styles.itemContainer}>
              <MarkdownRenderer
                content={`${t("collaborative.step")} ${step.step_number}: ${renderContent(step.title)}`}
                customStyle={{
                  text: {
                    text: {
                      ...styles.contentText,
                      fontSize: scaleFontSize(
                        styles.contentText.fontSize,
                        customizationSettings?.fontSize,
                      ),
                      fontWeight: "bold",
                    },
                    container: {
                      marginBottom: 4,
                    },
                  },
                }}
              />
              <MarkdownRenderer
                content={renderContent(step.description)}
                customStyle={{
                  text: {
                    text: {
                      ...styles.contentText,
                      fontSize: scaleFontSize(
                        styles.contentText.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                    container: {
                      marginBottom: 4,
                    },
                  },
                }}
              />
              <MarkdownRenderer
                content={
                  t("collaborative.timeEstimate") +
                  ": " +
                  renderContent(step.time_estimate)
                }
                customStyle={{
                  text: {
                    text: {
                      ...styles.contentText,
                      fontSize: scaleFontSize(
                        styles.contentText.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                    container: {
                      marginBottom: 4,
                    },
                  },
                }}
              />
              <MarkdownRenderer
                content={
                  t("collaborative.deliverables") +
                  ": " +
                  renderContent(step.deliverables)
                }
                customStyle={{
                  text: {
                    text: {
                      ...styles.contentText,
                      fontSize: scaleFontSize(
                        styles.contentText.fontSize,
                        customizationSettings?.fontSize,
                      ),
                    },
                    container: {
                      marginBottom: 4,
                    },
                  },
                }}
              />
            </View>
          ))}
        </View>
      )}

      {/* Teacher Role Description Section */}
      {collaborative.teacherRoleDescription && (
        <View style={[styles.section, styles.teacherRoleSection]} wrap={false}>
          <MarkdownRenderer
            content={t("collaborative.teacherRoleDescription")}
            customStyle={{
              text: {
                text: {
                  ...styles.sectionTitle,
                  ...styles.teacherRoleTitle,
                  fontSize: scaleFontSize(
                    styles.sectionTitle.fontSize,
                    customizationSettings?.fontSize,
                  ),
                },
                container: {
                  marginBottom: 8,
                },
              },
            }}
          />
          <View style={styles.itemContainer}>
            <MarkdownRenderer
              content={renderContent(collaborative.teacherRoleDescription)}
              customStyle={{
                text: {
                  text: {
                    ...styles.contentText,
                    fontSize: scaleFontSize(
                      styles.contentText.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                  container: {
                    marginBottom: 4,
                  },
                },
              }}
            />
          </View>
        </View>
      )}

      {/* Teacher Evaluation Questions Section */}
      {collaborative.teacherEvaluationQuestions &&
        collaborative.teacherEvaluationQuestions.length > 0 && (
          <View
            style={[styles.section, styles.teacherEvaluationSection]}
            wrap={false}
          >
            <MarkdownRenderer
              content={t("collaborative.teacherEvaluationQuestions")}
              customStyle={{
                text: {
                  text: {
                    ...styles.sectionTitle,
                    ...styles.teacherEvaluationTitle,
                    fontSize: scaleFontSize(
                      styles.sectionTitle.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                  container: {
                    marginBottom: 8,
                  },
                },
              }}
            />
            {collaborative.teacherEvaluationQuestions.map((item, index) => (
              <View key={index} style={styles.itemContainer}>
                <MarkdownRenderer
                  content={
                    t("collaborative.question") +
                    ": " +
                    renderContent(item.question)
                  }
                  customStyle={{
                    text: {
                      text: {
                        ...styles.contentText,
                        fontSize: scaleFontSize(
                          styles.contentText.fontSize,
                          customizationSettings?.fontSize,
                        ),
                        fontWeight: "bold",
                      },
                      container: {
                        marginBottom: 4,
                      },
                    },
                  }}
                />
                {(item as any).focus_area && (
                  <MarkdownRenderer
                    content={
                      t("collaborative.focusArea") +
                      ": " +
                      renderContent((item as any).focus_area)
                    }
                    customStyle={{
                      text: {
                        text: {
                          ...styles.contentText,
                          fontSize: scaleFontSize(
                            styles.contentText.fontSize,
                            customizationSettings?.fontSize,
                          ),
                        },
                        container: {
                          marginBottom: 4,
                        },
                      },
                    }}
                  />
                )}
                {(item as any).observation_criteria && (
                  <MarkdownRenderer
                    content={
                      t("collaborative.observationCriteria") +
                      ": " +
                      renderContent((item as any).observation_criteria)
                    }
                    customStyle={{
                      text: {
                        text: {
                          ...styles.contentText,
                          fontSize: scaleFontSize(
                            styles.contentText.fontSize,
                            customizationSettings?.fontSize,
                          ),
                        },
                        container: {
                          marginBottom: 4,
                        },
                      },
                    }}
                  />
                )}
              </View>
            ))}
          </View>
        )}

      {/* Process Evaluation Criteria Section */}
      {collaborative.processEvaluationCriteria &&
        collaborative.processEvaluationCriteria.length > 0 && (
          <View
            style={[styles.section, styles.processEvaluationSection]}
            wrap={false}
          >
            <MarkdownRenderer
              content={t("collaborative.processEvaluationCriteria")}
              customStyle={{
                text: {
                  text: {
                    ...styles.sectionTitle,
                    ...styles.processEvaluationTitle,
                    fontSize: scaleFontSize(
                      styles.sectionTitle.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                  container: {
                    marginBottom: 8,
                  },
                },
              }}
            />
            {collaborative.processEvaluationCriteria.map((item, index) => (
              <View key={index} style={styles.itemContainer}>
                {(item as any).criteria_name && (
                  <MarkdownRenderer
                    content={
                      t("collaborative.criteriaName") +
                      ": " +
                      renderContent((item as any).criteria_name)
                    }
                    customStyle={{
                      text: {
                        text: {
                          ...styles.contentText,
                          fontSize: scaleFontSize(
                            styles.contentText.fontSize,
                            customizationSettings?.fontSize,
                          ),
                          fontWeight: "bold",
                        },
                        container: {
                          marginBottom: 4,
                        },
                      },
                    }}
                  />
                )}
                {item.description && (
                  <MarkdownRenderer
                    content={
                      t("collaborative.description") +
                      ": " +
                      renderContent(item.description)
                    }
                    customStyle={{
                      text: {
                        text: {
                          ...styles.contentText,
                          fontSize: scaleFontSize(
                            styles.contentText.fontSize,
                            customizationSettings?.fontSize,
                          ),
                        },
                        container: {
                          marginBottom: 4,
                        },
                      },
                    }}
                  />
                )}
                {(item as any).evaluation_method && (
                  <MarkdownRenderer
                    content={
                      t("collaborative.evaluationMethod") +
                      ": " +
                      renderContent((item as any).evaluation_method)
                    }
                    customStyle={{
                      text: {
                        text: {
                          ...styles.contentText,
                          fontSize: scaleFontSize(
                            styles.contentText.fontSize,
                            customizationSettings?.fontSize,
                          ),
                        },
                        container: {
                          marginBottom: 4,
                        },
                      },
                    }}
                  />
                )}
              </View>
            ))}
          </View>
        )}

      {/* Reflection Questions Section */}
      {collaborative.reflectionQuestions &&
        collaborative.reflectionQuestions.length > 0 && (
          <View style={[styles.section, styles.reflectionSection]} wrap={false}>
            <MarkdownRenderer
              content={t("collaborative.reflectionQuestions")}
              customStyle={{
                text: {
                  text: {
                    ...styles.sectionTitle,
                    ...styles.reflectionTitle,
                    fontSize: scaleFontSize(
                      styles.sectionTitle.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                  container: {
                    marginBottom: 8,
                  },
                },
              }}
            />
            {collaborative.reflectionQuestions.map((item, index) => (
              <View key={index} style={styles.itemContainer}>
                <MarkdownRenderer
                  content={
                    t("collaborative.question") +
                    ": " +
                    renderContent(item.question)
                  }
                  customStyle={{
                    text: {
                      text: {
                        ...styles.contentText,
                        fontSize: scaleFontSize(
                          styles.contentText.fontSize,
                          customizationSettings?.fontSize,
                        ),
                      },
                      container: {
                        marginBottom: 4,
                      },
                    },
                  }}
                />
                <MarkdownRenderer
                  content={
                    t("collaborative.purpose") +
                    ": " +
                    renderContent(item.purpose)
                  }
                  customStyle={{
                    text: {
                      text: {
                        ...styles.contentText,
                        fontSize: scaleFontSize(
                          styles.contentText.fontSize,
                          customizationSettings?.fontSize,
                        ),
                      },
                      container: {
                        marginBottom: 4,
                      },
                    },
                  }}
                />
              </View>
            ))}
          </View>
        )}

      {/* Peer Evaluation Section */}
      {collaborative.peerEvaluationQuestions &&
        collaborative.peerEvaluationQuestions.length > 0 && (
          <View style={[styles.section, styles.evaluationSection]} wrap={false}>
            <MarkdownRenderer
              content={t("collaborative.peerEvaluation")}
              customStyle={{
                text: {
                  text: {
                    ...styles.sectionTitle,
                    ...styles.evaluationTitle,
                    fontSize: scaleFontSize(
                      styles.sectionTitle.fontSize,
                      customizationSettings?.fontSize,
                    ),
                  },
                  container: {
                    marginBottom: 8,
                  },
                },
              }}
            />
            {collaborative.peerEvaluationQuestions.map((item, index) => (
              <View key={index} style={styles.itemContainer}>
                <MarkdownRenderer
                  content={
                    t("collaborative.question") +
                    ": " +
                    renderContent(item.question)
                  }
                  customStyle={{
                    text: {
                      text: {
                        ...styles.contentText,
                        fontSize: scaleFontSize(
                          styles.contentText.fontSize,
                          customizationSettings?.fontSize,
                        ),
                      },
                      container: {
                        marginBottom: 4,
                      },
                    },
                  }}
                />
                <MarkdownRenderer
                  content={
                    t("collaborative.criteria") +
                    ": " +
                    renderContent(item.criteria)
                  }
                  customStyle={{
                    text: {
                      text: {
                        ...styles.contentText,
                        fontSize: scaleFontSize(
                          styles.contentText.fontSize,
                          customizationSettings?.fontSize,
                        ),
                      },
                      container: {
                        marginBottom: 4,
                      },
                    },
                  }}
                />
                <MarkdownRenderer
                  content={
                    t("collaborative.scale") + ": " + renderContent(item.scale)
                  }
                  customStyle={{
                    text: {
                      text: {
                        ...styles.contentText,
                        fontSize: scaleFontSize(
                          styles.contentText.fontSize,
                          customizationSettings?.fontSize,
                        ),
                      },
                      container: {
                        marginBottom: 4,
                      },
                    },
                  }}
                />
              </View>
            ))}
          </View>
        )}
    </PdfLayout>
  );
};

function CollaborativeCardPDF(props: CollaborativeCardProps) {
  return <CollaborativeDocument {...props} />;
}

export default CollaborativeCardPDF;
